import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { type BuilderContent } from '@builder.io/sdk-angular';
import { BuilderService } from '../../services/builder.service';

/**
 * Resolver for fetching blog article data before route activation
 * This follows Builder.io best practices for Angular blog integration
 * Uses BuilderService which properly initializes the SDK
 * 
 * URL Structures:
 * 1. Production: /content-hub/:audience/ref/** or /content-hub/:audience/post/**
 *    Example: /content-hub/highschool/ref/communication-blog/article-name
 * 
 * 2. Builder.io Preview: /blog/**
 *    Example: /blog/ref/communication-blog/article-name
 * 
 * Builder.io Slug Format: /ref/communication-blog/article-name
 * The slug prefix (/ref or /post) is passed via route data and prepended to the path
 */
export const blogPageResolver: ResolveFn<BuilderContent | null> = async (route) => {
  const builderService = inject(BuilderService);
  
  // Get the slug prefix from parent route data (/ref, /post, or empty for preview)
  const slugPrefix = route.parent?.data?.['slugPrefix'];
  
  // route.url contains all path segments after the prefix (the wildcard match)
  // For URL: /content-hub/highschool/ref/communication-blog/article-name
  // route.url will be: ['communication-blog', 'article-name']
  const pathAfterPrefix = route.url.map(segment => segment.path).join('/');
  
  // Construct full slug
  let slug: string;
  
  if (slugPrefix !== undefined && slugPrefix !== null) {
    // Production route with explicit prefix
    slug = pathAfterPrefix ? `${slugPrefix}/${pathAfterPrefix}` : slugPrefix;
  } else {
    // Builder.io preview route - use path as-is (includes /ref or /post in the path)
    slug = pathAfterPrefix ? `/${pathAfterPrefix}` : '';
  }
  
  if (!slug) {
    return null;
  }

  // Fetch blog entry from Builder.io by slug using the service
  return await builderService.getBlogBySlug(slug);
};

